import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import {
  FieldValues,
  UseFormProps,
  UseFormReturn,
  useForm as useReactHookForm,
  WatchObserver,
} from 'react-hook-form';
import { z } from 'zod';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const debounce = <T extends (...args: any[]) => void>(
  func: T,
  wait: number,
) => {
  let timeout: ReturnType<typeof setTimeout>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

export const useForm = <FormSchema extends z.ZodTypeAny>({
  schema,
  ...props
}: UseFormProps<z.infer<FormSchema>> & { schema: FormSchema }) => {
  const form = useReactHookForm<z.infer<FormSchema>>({
    ...props,
    resolver: zodResolver(schema),
  });
  return form;
};

export const useChangeSubmission = <Fields extends FieldValues>(
  form: UseFormReturn<Fields>,
  onSubmit: (data: Fields) => void,
  wait?: number,
) => {
  const watch = form.watch;
  const handleSubmit = form.handleSubmit;
  useEffect(() => {
    const subscription = watch(
      debounce(
        (_, { name }) => {
          if (name) {
            handleSubmit(onSubmit)();
          }
        },
        wait ? wait : 2500,
      ),
    );
    return () => subscription.unsubscribe();
  }, [watch, onSubmit, wait, handleSubmit]);
};

export const useWatchSubmission = <Fields extends FieldValues>(
  form: UseFormReturn<Fields>,
  onSubmit: (
    data: Parameters<WatchObserver<Fields>>[1] & {
      values: Parameters<WatchObserver<Fields>>[0];
    },
  ) => void,
  wait?: number,
) => {
  const watch = form.watch;
  const handleSubmit = form.handleSubmit;
  useEffect(() => {
    const subscription = watch(
      debounce(
        (_, info) => {
          if (info && 'name' in info && info.name) {
            handleSubmit(() => onSubmit(info))();
          }
        },
        wait ? wait : 2500,
      ),
    );
    return () => subscription.unsubscribe();
  }, [watch, onSubmit, wait, handleSubmit]);
};
